import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, Button, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import colors from '../../theme/colors';
import { getMeetups, Meetup } from '../../services/firebaseService';
import HeroSection from './HeroSection';
import FooterSection from './FooterSection';
import EventCard from './EventCard';
import MeetupModal from './MeetupModal';

const AllEventsPage: React.FC = () => {
  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMeetup, setSelectedMeetup] = useState<Meetup | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMeetups = async () => {
      try {
        setLoading(true);
        const fetchedMeetups = await getMeetups();
        setMeetups(fetchedMeetups);
      } catch (err) {
        setError('Failed to fetch meetups. Please try again later.');
        console.error('Error fetching meetups:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMeetups();
  }, []);

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleOpenModal = (meetup: Meetup) => {
    setSelectedMeetup(meetup);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedMeetup(null);
  };



  if (loading) {
    return (
      <>
        <HeroSection />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: colors.green }} />
          <Typography variant="h6" sx={{ mt: 2, color: colors.textSecondary }}>
            Loading events...
          </Typography>
        </Container>
        <FooterSection />
      </>
    );
  }

  if (error) {
    return (
      <>
        <HeroSection />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Container>
        <FooterSection />
      </>
    );
  }

  return (
    <>
      <HeroSection />
      <Container maxWidth="lg" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h3" color="primary" fontWeight={700} gutterBottom>
              All Events
            </Typography>
            <Box sx={{ height: 4, width: 48, borderRadius: 2, background: colors.accent, mb: 2 }} />
            <Typography variant="body1" color={colors.textSecondary}>
              Discover all our past and upcoming events
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleBackToHome}
            sx={{ fontWeight: 600 }}
          >
            Back to Home
          </Button>
        </Box>

        {meetups.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color={colors.textSecondary} gutterBottom>
              No meetups found
            </Typography>
            <Typography variant="body2" color={colors.textSecondary}>
              Check back later for upcoming meetups!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {meetups.map((meetup) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={meetup.id || meetup.title}>
                <EventCard 
                  meetup={meetup} 
                  onReadMore={handleOpenModal}
                  showReadMore={true}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Meetup Details Modal */}
      <MeetupModal
        open={modalOpen}
        onClose={handleCloseModal}
        meetup={selectedMeetup}
      />

      <FooterSection />
    </>
  );
};

export default AllEventsPage;
